/**
 * The first thing every message needs (boards t5xf, jzo5): finding a block in a
 * reply and telling it from code. It imports nothing. What a block *means* is in
 * `./ui-block-canonical`, also eager; zod is in `./ui-blocks`, OpenUI's parser in
 * `./ui-block-lang` and the old JSON format in `./ui-block-legacy`, all loaded only
 * when a block is on screen.
 */

/** Where HUB's stored blocks live: a fence of canonical OpenUI Lang. */
export const UI_BLOCK_FENCE = 'openui'

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const UI_BLOCK_LIMITS = {
  /** UTF-8 is not measured: characters of the fence body. */
  specChars: 64_000,
  elements: 12,
  rows: 500,
  columns: 24,
  categories: 50,
  series: 8,
} as const

export type UiBlockCell = string | number | null

export const UI_BLOCK_COLUMN_TYPES = ['text', 'number', 'date', 'currency'] as const
export type UiBlockColumnType = (typeof UI_BLOCK_COLUMN_TYPES)[number]

const TABLE_SEPARATOR = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/

/** Pipe tables in `text`: a separator row that has a pipe and a row above it. */
export function countMarkdownTables(text: string): number {
  const lines = text.split('\n')
  return lines.filter(
    (line, i) => line.includes('|') && TABLE_SEPARATOR.test(line) && lines[i - 1]?.includes('|')
  ).length
}

// ---------------------------------------------------------------------------
// The migration's report (board jzo5): ids and counts, never content
// ---------------------------------------------------------------------------

/** A message the migration wants a human to look at. */
export interface UiBlocksMigrationMessage {
  projectId: string
  threadId: string
  messageId: string
  outcome: 'converted' | 'markdown' | 'dropped'
  /** Elements v1.11.0 stored and never drew: conversion leaves them out. */
  unreachable: number
}

export interface UiBlocksMigrationReport {
  dryRun: boolean
  projects: number
  threads: number
  /** Threads with at least one message holding an old block. */
  threadsWithBlocks: number
  messages: number
  /** `notUi`: ```spec fences that are code (RSpec, OpenAPI), left alone. */
  blocks: { converted: number; markdown: number; dropped: number; notUi: number }
  unreachable: number
  written: number
  /** Lost a compare-and-set to a concurrent change: the next run gets it. */
  skipped: number
  failedThreads: string[]
  listed: UiBlocksMigrationMessage[]
  /** `listed` is capped; this is how many there were. */
  listedTotal: number
}

/** One thread's share: the workflow adds the project, the thread and the run. */
export type UiBlocksThreadReport = Pick<
  UiBlocksMigrationReport,
  'messages' | 'blocks' | 'unreachable' | 'written' | 'skipped' | 'listedTotal'
> & { listed: Omit<UiBlocksMigrationMessage, 'projectId' | 'threadId'>[] }

// ---------------------------------------------------------------------------
// Finding OpenUI Lang in a reply
// ---------------------------------------------------------------------------

/** Readers also take OpenUI's own tag; HUB stores `openui`. */
export const OPENUI_FENCES = [UI_BLOCK_FENCE, 'openui-lang']

const COMMENT = /^\s*(#|\/\/)/
const STATEMENT_START = /^\s*[A-Za-z_$][\w$]*\s*=(?!=)/
/**
 * `x = 5` in a fence is Python: a block has a root or a catalog component. Either
 * may come after a line like `rows = [["a", 1]]`, so any line of the body counts.
 */
const BLOCK_LINE = /^\s*(root\s*=|[A-Za-z_]\w*\s*=[\s[]*(Stack|DataTable|Chart|Stat|Col|Series)\()/
const BARE_ROOT = /^root\s*=\s*[A-Z]\w*\(/

/** The body of an ```openui fence that is a UI block, or still can become one. */
export function looksLikeOpenUi(source: string): boolean {
  const lines = source.split('\n').filter((line) => line.trim() && !COMMENT.test(line))
  const first = lines[0]
  if (first === undefined) return true
  if (lines.some((line) => BLOCK_LINE.test(line))) return true
  // The first line is still arriving.
  const text = first.trim()
  return !source.trim().includes('\n') && 'root ='.startsWith(text)
}

interface Scan {
  depth: number
  inString: boolean
  quote: string
  escaped: boolean
}

function scanLine(line: string, scan: Scan): void {
  for (const ch of line) {
    if (scan.inString) {
      if (scan.escaped) scan.escaped = false
      else if (ch === '\\') scan.escaped = true
      else if (ch === scan.quote) scan.inString = false
    } else if (ch === '"' || ch === "'") {
      scan.inString = true
      scan.quote = ch
    } else if ('([{'.includes(ch)) scan.depth++
    else if (')]}'.includes(ch)) scan.depth--
  }
}

/** How many of `lines` are statements: the first prose line at depth 0 ends the run. */
function statementRun(lines: string[]): number {
  const scan: Scan = { depth: 0, inString: false, quote: '"', escaped: false }
  let end = 0
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!
    const between = scan.depth <= 0 && !scan.inString
    if (between && !line.trim()) continue
    if (between && !COMMENT.test(line) && !STATEMENT_START.test(line)) break
    scanLine(line, scan)
    end = i + 1
  }
  return end
}

/** Names of the statements in `source`, in order, repeats included. */
export function statementNames(source: string): string[] {
  const scan: Scan = { depth: 0, inString: false, quote: '"', escaped: false }
  const names: string[] = []
  for (const line of source.split('\n')) {
    if (scan.depth <= 0 && !scan.inString && !COMMENT.test(line)) {
      const name = /^\s*([A-Za-z_$][\w$]*)\s*=(?!=)/.exec(line)?.[1]
      if (name) names.push(name)
    }
    scanLine(line, scan)
  }
  return names
}

export interface FoundUiBlock {
  /** `content.slice(start, end)` is the block, fence lines included. */
  start: number
  end: number
  source: string
  /** The closing fence arrived (always true for bare blocks). */
  closed: boolean
  /** No fence around it (a model slip). */
  bare: boolean
  /** No closing fence, and something else follows `end`. */
  proseAfter: boolean
}

const FENCE = /^ {0,3}(`{3,}|~{3,})\s*([\w-]*)/
const closesFence = (line: string, marker: string) => {
  const trimmed = line.trim()
  return trimmed.length >= marker.length && trimmed === marker[0]!.repeat(trimmed.length)
}

/**
 * HUB finds blocks itself: OpenUI's own scan would take any fence whose first
 * line looks like `x = …`. Every other fence is skipped whole, so a block's
 * syntax inside a code sample stays a code sample.
 */
export function findOpenUiBlocks(content: string): FoundUiBlock[] {
  const blocks: FoundUiBlock[] = []
  const lines = content.split('\n')
  const clean = (index: number) => lines[index]!.replace(/\r$/, '')
  const offsets: number[] = []
  let offset = 0
  for (const line of lines) {
    offsets.push(offset)
    offset += line.length + 1
  }
  const endOf = (index: number) => offsets[index]! + lines[index]!.length

  let i = 0
  while (i < lines.length) {
    const open = FENCE.exec(clean(i))
    if (open) {
      const marker = open[1]!
      let close = i + 1
      while (close < lines.length && !closesFence(clean(close), marker)) close++
      const closed = close < lines.length
      const isOpenUi = OPENUI_FENCES.includes(open[2]!)
      const body = lines.slice(i + 1, close).map((line) => line.replace(/\r$/, ''))
      if (isOpenUi && looksLikeOpenUi(body.join('\n'))) {
        const run = closed ? body.length : statementRun(body)
        const proseAfter = !closed && body.slice(run).some((line) => line.trim())
        const last = closed ? close : i + run
        blocks.push({
          start: offsets[i]!,
          end: proseAfter || closed ? endOf(last) : content.length,
          source: body.slice(0, run).join('\n').trimEnd(),
          closed,
          bare: false,
          proseAfter,
        })
        i = proseAfter ? last + 1 : closed ? close + 1 : lines.length
        continue
      }
      i = closed ? close + 1 : lines.length
      continue
    }
    if (BARE_ROOT.test(clean(i))) {
      const rest = lines.slice(i).map((line) => line.replace(/\r$/, ''))
      const run = statementRun(rest)
      const last = i + run - 1
      blocks.push({
        start: offsets[i]!,
        end: endOf(last),
        source: rest.slice(0, run).join('\n').trimEnd(),
        closed: true,
        bare: true,
        proseAfter: false,
      })
      i = last + 1
      continue
    }
    i++
  }
  return blocks
}

export const openUiFence = (source: string) => `\`\`\`${UI_BLOCK_FENCE}\n${source}\n\`\`\``

/** `content` without its blocks: what the model wrote as prose. */
export function uiBlockProse(content: string, blocks: { start: number; end: number }[]): string {
  let out = ''
  let at = 0
  for (const block of [...blocks].sort((a, b) => a.start - b.start)) {
    out += content.slice(at, block.start)
    at = block.end
  }
  return out + content.slice(at)
}

const LEGACY_FENCE = /^ {0,3}(`{3,}|~{3,})\s*spec\s*$/m
const LEGACY_LINE = /^\s*\{\s*"op"\s*:/

/** Might hold a block of the old JSON format: the cheap test before loading its reader. */
export const hasLegacyUiBlock = (content: string) =>
  LEGACY_FENCE.test(content) || content.split('\n').some((line) => LEGACY_LINE.test(line))

/** The body of a ```spec fence that is a UI block rather than an RSpec snippet. */
export function looksLikeLegacyUiBlock(source: string): boolean {
  const first =
    source
      .split('\n')
      .find((line) => line.trim())
      ?.trim() ?? ''
  // The first line may still be arriving.
  return LEGACY_LINE.test(first) || (!source.trim().includes('\n') && '{"op"'.startsWith(first))
}

/** `content` with old blocks left out, for when their reader cannot be had. */
export const withoutLegacyUiBlocks = (content: string) =>
  content
    .replace(/^ {0,3}(`{3,}|~{3,})\s*spec\s*$[\s\S]*?(^ {0,3}\1[`~]*\s*$|(?![\s\S]))/gm, '')
    .replace(/^\s*\{\s*"op"\s*:.*$/gm, '')

/**
 * Bare patch lines from before this format get a fence, so the legacy block
 * draws them; prose after an open block ends it, or it would render as code.
 */
function fenceBareLegacyLines(content: string): string {
  if (!content.includes('"op"')) return content
  const lines = content.split('\n')
  const out: string[] = []
  let marker: string | null = null
  let oldBlock = false
  let run: string[] = []
  const flush = () => {
    if (run.length > 0) out.push('```spec', ...run, '```')
    run = []
  }
  for (const [index, line] of lines.entries()) {
    const open = FENCE.exec(line)
    if (marker) {
      if (closesFence(line, marker)) marker = null
      else if (oldBlock && line.trim() && !/^\s*(\{|<\/)/.test(line)) {
        out.push(marker)
        marker = null
      }
      out.push(line)
    } else if (open) {
      flush()
      marker = open[1]!
      oldBlock = open[2] === 'spec' && LEGACY_LINE.test(lines[index + 1] ?? '')
      out.push(line)
    } else if (LEGACY_LINE.test(line)) run.push(line)
    else {
      flush()
      out.push(line)
    }
  }
  flush()
  return out.join('\n')
}

/**
 * Text markdown can render: a fence around bare lines, and a closing fence where
 * prose followed an open one (or the prose would render as code).
 */
export function normalizeUiBlockFences(content: string): string {
  let out = ''
  let at = 0
  for (const block of findOpenUiBlocks(content)) {
    if (!block.bare && !block.proseAfter) continue
    out += content.slice(at, block.start) + openUiFence(block.source)
    at = block.end
  }
  return fenceBareLegacyLines(out + content.slice(at))
}
